import React, { useMemo, useState } from 'react'

import { Box, List, Stack, Typography, useTheme } from '@mui/material'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { DrawerPopup } from 'components/UI/DrawerPopup'
import { Input } from 'components/UI/Input'
import { PickerListItem } from 'components/UI/PickerListItem'
import { PICKER_LIST_PADDING } from 'components/UI/pickerListStyle'
import { FIELD_HEIGHT, fieldSurfaceSx } from 'components/UI/fieldMetrics'
import { space } from 'settings/tokens'

export interface SelectDrawerOption {
  value: string
  label: string
  /** Optional emoji flag (e.g. "🇺🇸") rendered before the label — OS font, no network. */
  flag?: string
  /** Optional flag/icon URL rendered before the label (fallback if no emoji). */
  iconUrl?: string
  /** Optional trailing detail (e.g. dial code). */
  secondary?: string
}

interface SelectDrawerProps {
  value: string | null
  onChange: (value: string) => void
  options: SelectDrawerOption[]
  /** Sheet title (e.g. "Select country"). */
  title: string
  /** Trigger placeholder when nothing is selected. */
  placeholder: string
  /** Show a search box at the top of the sheet (for long lists). */
  searchable?: boolean
  searchPlaceholder?: string
  error?: boolean
  /** Compact trigger (flag + secondary/label + chevron) — for inline use e.g. a phone dial-code picker. */
  compact?: boolean
}

/**
 * Mobile-native single-select: a field trigger that opens a bottom-sheet
 * (`DrawerPopup`) list — thumb-reachable, full-width targets, tap-to-select-and-
 * dismiss with a checkmark on the current value. Replaces floating dropdowns on
 * mobile (matches the profile design's per-field edit drawers). Optional search
 * for long lists (Country); flags via `iconUrl`.
 */
export const SelectDrawer: React.FC<SelectDrawerProps> = ({
  value,
  onChange,
  options,
  title,
  placeholder,
  searchable = false,
  searchPlaceholder = 'Search',
  error = false,
  compact = false,
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const selected = useMemo(() => options.find(o => o.value === value), [options, value])

  const filtered = useMemo(() => {
    if (!searchable || !search.trim()) return options
    const q = search.toLowerCase()
    return options.filter(o => o.label.toLowerCase().includes(q))
  }, [options, search, searchable])

  const brandColor = theme.palette.primary.main

  const handleSelect = (v: string) => {
    onChange(v)
    setOpen(false)
    setSearch('')
  }

  const flagNode = selected?.flag ? (
    <Typography sx={{ fontSize: '18px', flexShrink: 0, lineHeight: 1 }}>{selected.flag}</Typography>
  ) : (
    selected?.iconUrl && (
      <Box
        component='img'
        src={selected.iconUrl}
        alt=''
        sx={{ width: 20, height: 15, objectFit: 'cover', borderRadius: '2px', flexShrink: 0 }}
      />
    )
  )

  return (
    <>
      {compact ? (
        // Compact trigger (flag + dial code / short label) — inline, e.g. phone picker.
        <Stack
          direction='row'
          alignItems='center'
          gap={0.25}
          onClick={() => setOpen(true)}
          sx={{ height: '100%', pl: 1.5, pr: 0.5, cursor: 'pointer', flexShrink: 0 }}
        >
          {flagNode}
          <KeyboardArrowDownIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
        </Stack>
      ) : (
        // Full field trigger — matches the app's select fields.
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          onClick={() => setOpen(true)}
          sx={{
            // The shared field surface — radius CLAMPED, token border, token fill.
            // It was `theme.shape.borderRadius` verbatim (10px on this tenant) with
            // a `grey[300]` border, i.e. the same two bypasses the picker search
            // boxes carried, on the control that sits directly beside them.
            ...fieldSurfaceSx(theme),
            ...(error ? { border: `1px solid ${theme.palette.error.main} !important` } : null),
            height: FIELD_HEIGHT,
            // 16, matching `FieldTrigger` and `Input`. Was 12 (`px: 1.5`), so a
            // select's label started 4px left of the text field above it.
            px: `${space.lg}px`,
            cursor: 'pointer',
            '&:hover': { borderColor: error ? theme.palette.error.main : brandColor },
          }}
        >
          <Stack direction='row' alignItems='center' gap={1} sx={{ minWidth: 0 }}>
            {flagNode}
            <Typography
              noWrap
              sx={{
                fontSize: '16px',
                color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
              }}
            >
              {selected?.label ?? placeholder}
            </Typography>
          </Stack>
          <KeyboardArrowDownIcon sx={{ color: theme.palette.text.secondary, flexShrink: 0 }} />
        </Stack>
      )}

      {open && (
        <DrawerPopup
          title={title}
          titleUppercase={false}
          fullScreen={false}
          backgroundColor={theme.palette.background.paper}
          titleSx={{ color: theme.palette.text.primary, fontSize: '20px', fontWeight: 'normal' }}
          handleOpen={open}
          handleClose={() => {
            setOpen(false)
            setSearch('')
          }}
          child={
            <Stack gap={2} sx={{ maxWidth: 460, width: '100%', mx: 'auto' }}>
              {searchable && (
                <Input
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={setSearch}
                  fullWidth
                  autoFocus
                  // Bare — `Input` owns the adornment colour and gap.
                  startAdornment={<SearchOutlinedIcon sx={{ fontSize: 16 }} />}
                />
              )}
              <List sx={{ maxHeight: '60vh', overflow: 'auto', px: 0, py: PICKER_LIST_PADDING }}>
                {filtered.map(opt => {
                  const isSelected = opt.value === value
                  const leading = opt.flag ? (
                    <Typography sx={{ fontSize: '18px', flexShrink: 0, lineHeight: 1 }}>{opt.flag}</Typography>
                  ) : (
                    opt.iconUrl && (
                      <Box
                        component='img'
                        src={opt.iconUrl}
                        alt=''
                        sx={{ width: 20, height: 15, objectFit: 'cover', borderRadius: '2px', flexShrink: 0 }}
                      />
                    )
                  )
                  return (
                    <PickerListItem
                      key={opt.value}
                      label={opt.label}
                      leading={leading}
                      trailing={opt.secondary}
                      selected={isSelected}
                      onClick={() => handleSelect(opt.value)}
                    />
                  )
                })}
              </List>
            </Stack>
          }
        />
      )}
    </>
  )
}
